import React, { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../auth/authClient";
import {
  getEmptyZakazkaForm,
  normalizePublicZakazkaPayload,
  validatePublicZakazka,
} from "./udrzbaZakazkaShared";
import UdrzbaGallerySection from "./UdrzbaGallerySection";
import {
  HROBY_PUBLIC,
  HLAVNI_SLUZBY_PUBLIC,
  LIMITY_KVETINY,
  LIMITY_DEKORACE,
  getHlavniSluzbaById,
  getZakladniCenu,
  formatCena,
} from "./udrzbaPublicCenik";

const API = "/api/udrzba";

const inputStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  fontSize: 16,
  boxSizing: "border-box",
  background: "#fff",
};

const sectionBoxStyle = {
  marginTop: 22,
  padding: 22,
  borderRadius: 22,
  border: "1px solid #dbe7f5",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
};

const cardStyle = {
  width: "100%",
  maxWidth: 760,
  margin: "0 auto",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 22,
  padding: 20,
  boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
  boxSizing: "border-box",
};

const serviceOptions = [
  "Jednorázové čištění hrobu",
  "Pravidelná údržba hrobu",
  "Základní péče o hrob – 4 návštěvy + 5. zdarma",
  "Mytí náhrobní desky",
  "Dosypání kamínků",
  "Výsadba květin",
  "Zalití a běžná péče",
  "Úklid okolí hrobu",
];

const JMENA_K_SVATKU = {
  marie: "2026-09-12",
  josef: "2026-03-19",
  anna: "2026-07-26",
  jan: "2026-06-24",
  jana: "2026-05-24",
  petr: "2026-06-29",
  pavel: "2026-06-29",
  vera: "2026-02-08",
  veronika: "2026-02-07",
  tereza: "2026-10-15",
  barbora: "2026-12-04",
  katerina: "2026-11-25",
  katerina2: "2026-11-25",
  frantisek: "2026-10-04",
  alena: "2026-08-13",
  eva: "2026-12-24",
  adam: "2026-12-24",
  michael: "2026-09-29",
  michal: "2026-09-29",
  lucie: "2026-12-13",
};

function normalizeName(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatCzDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return "";
  return `${d}.${m}.${y}`;
}

function getYearFromForm(form) {
  const fromPlan = String(form?.planovaneDatum || "").slice(0, 10);
  if (fromPlan) return Number(fromPlan);
  return new Date().getFullYear();
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function withYear(dateLike, year) {
  if (!dateLike) return "";
  const parts = String(dateLike).split("-");
  if (parts.length !== 3) return "";
  return `${year}-${parts[1]}-${parts[2]}`;
}

function getVyrociDate(sourceDate, year) {
  if (!sourceDate) return "";
  return withYear(sourceDate, year);
}

function formatDateForInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}


function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function getJmeninyDate(form, year) {
  const rawName =
    String(form?.jmenoNaDesce || "").trim().split(/\s+/)[0] ||
    String(form?.majitelHrobuJmeno || "").trim().split(/\s+/)[0] ||
    "";
  const key = normalizeName(rawName);
  const found = JMENA_K_SVATKU[key] || "";
  return found ? withYear(found, year) : "";
}



function getAnniversaryDate(value, year) {
  const x = String(value || "").trim();
  if (!x) return "";

  let day = null;
  let month = null;

  const iso = x.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else {
    const cz = x.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (cz) {
      day = Number(cz[1]);
      month = Number(cz[2]);
    } else {
      return "";
    }
  }

  try {
    if (month === 2 && day === 29) {
      const isLeap = new Date(year, 1, 29).getDate() === 29;
      if (!isLeap) day = 28;
    }

    const d = new Date(year, month - 1, day);
    if (Number.isNaN(d.getTime())) return "";

    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch (e) {
    return "";
  }
}

function getTargetYear(form) {
  const candidates = [
    form?.planovaneDatum,
    ...(Array.isArray(form?.balicekTerminy) ? form.balicekTerminy : []),
    form?.zesnulyDatumUmrti,
    form?.zesnulyDatumNarozeni,
  ];

  for (const value of candidates) {
    const x = String(value || "").trim();
    if (!x) continue;

    const iso = x.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return Number(iso[1]);

    const cz = x.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (cz) return Number(cz[3]);

    const yearOnly = x.match(/^(\d{4})$/);
    if (yearOnly) return Number(yearOnly[1]);
  }

  return new Date().getFullYear();
}


function getSelectedImportantDates(selectedKeys, options) {
  const out = [];
  const keys = Array.isArray(selectedKeys) ? selectedKeys : [];

  for (const key of keys) {
    const found = Array.isArray(options) ? options.find((x) => x.key === key) : null;
    const datum = String(found?.datum || "").trim();
    if (!datum) continue;
    if (!out.includes(datum)) out.push(datum);
  }

  return out.sort((a, b) => a.localeCompare(b));
}

function getAllImportantDates(options) {
  const out = [];
  for (const item of Array.isArray(options) ? options : []) {
    const datum = String(item?.datum || "").trim();
    if (!datum) continue;
    if (!out.includes(datum)) out.push(datum);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

function getVyznamneDny(form) {
  const year = getTargetYear(form);
  const out = [
    {
      key: "velikonoce",
      label: "Velikonoce",
      datum: formatDateForInput(getEasterSunday(year)),
      alwaysVisible: true,
    },
    {
      key: "dusicky",
      label: "Dušičky",
      datum: formatDateForInput(new Date(year, 10, 2)),
      alwaysVisible: true,
    },
    {
      key: "vanoce",
      label: "Vánoce",
      datum: formatDateForInput(new Date(year, 11, 24)),
      alwaysVisible: true,
    },
  ];

  const umrtiVyroci = getAnniversaryDate(form?.zesnulyDatumUmrti, year);
  if (umrtiVyroci) {
    out.push({
      key: "umrti_vyroci",
      label: "Výročí úmrtí",
      datum: umrtiVyroci,
      alwaysVisible: false,
    });
  }

  const narozeniVyroci = getAnniversaryDate(form?.zesnulyDatumNarozeni, year);
  if (narozeniVyroci) {
    out.push({
      key: "narozeni_vyroci",
      label: "Výročí narození",
      datum: narozeniVyroci,
      alwaysVisible: false,
    });
  }

  const jmeniny = getJmeninyDate(form, year);
  if (jmeniny) {
    out.push({
      key: "jmeniny",
      label: "Jmeniny",
      datum: jmeniny,
      alwaysVisible: false,
    });
  }

  return out.filter((x) => x.datum);
}

export default function UdrzbaVerejnyForm() {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState(() =>
    getEmptyZakazkaForm({
      zesnulyDatumNarozeni: "",
      balicekTerminy: ["", "", "", "", "", ""],
      vyznamneDny: [],
    })
  );
  const [balicekVisibleCount, setBalicekVisibleCount] = useState(5);

  const isBalicekSelected =
    form.hlavniSluzba === "balicek_4_plus_1"
    || (Array.isArray(form.sluzby) && form.sluzby.includes("Základní péče o hrob – 4 návštěvy + 5. zdarma"));

  const isPredplatneSelected =
    String(form.hlavniSluzba || "").includes("pravidelna")
    || String(form.hlavniSluzba || "").includes("predplatne");

  useEffect(() => {
    if (!isBalicekSelected) return;
    setBalicekVisibleCount((prev) => (prev < 5 ? 5 : prev));
  }, [isBalicekSelected]);

  const vyznamneDnyOptions = useMemo(() => getVyznamneDny(form), [form]);

  const allImportantDates = useMemo(() => {
    return getAllImportantDates(vyznamneDnyOptions);
  }, [vyznamneDnyOptions]);

  const rucniTerminyOptions = useMemo(() => {
    const out = [];
    const blocked = new Set(allImportantDates);

    for (const raw of Array.isArray(form.balicekTerminy) ? form.balicekTerminy : []) {
      const datum = String(raw || "").trim();
      if (!datum) continue;
      if (blocked.has(datum)) continue;
      if (!out.includes(datum)) out.push(datum);
    }

    return out.sort((a, b) => a.localeCompare(b));
  }, [form.balicekTerminy, allImportantDates]);

  useEffect(() => {
    if (!isBalicekSelected) return;

    const current = Array.isArray(form.balicekTerminy) ? form.balicekTerminy : ["", "", "", "", "", ""];
    const selectedDates = getSelectedImportantDates(form.vyznamneDny, vyznamneDnyOptions);
    const blocked = new Set(allImportantDates);

    const manualDates = [];
    for (const d of current) {
      const x = String(d || "").trim();
      if (!x) continue;
      if (selectedDates.includes(x)) continue;
      if (blocked.has(x)) continue;
      if (!manualDates.includes(x)) manualDates.push(x);
    }

    const next = [...selectedDates];
    for (const d of manualDates) {
      if (next.length >= 6) break;
      if (!next.includes(d)) next.push(d);
    }

    while (next.length < 6) next.push("");

    if (JSON.stringify(next) !== JSON.stringify(current)) {
      setForm((prev) => ({
        ...prev,
        balicekTerminy: next,
      }));
    }
  }, [isBalicekSelected, form.vyznamneDny, vyznamneDnyOptions, allImportantDates]);

  const balicekNavstevy = useMemo(() => {
    const dates = (Array.isArray(form.balicekTerminy) ? form.balicekTerminy : [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);

    const current = Array.isArray(form.balicekNavstevy) ? form.balicekNavstevy : [];

    return dates.map((datum, index) => {
      const found = current.find((x) => String(x?.datum || "").slice(0, 10) === datum) || {};
      const kvetinyCastka = Math.round(Number(found?.kvetinyCastka || 0) * 100) / 100;
      const dekoraceCastka = Math.round(Number(found?.dekoraceCastka || 0) * 100) / 100;

      return {
        id: String(found?.id || `balicek-navsteva-${index + 1}`),
        datum,
        poradi: index + 1,
        jeZdarma: index >= 4,
        kvetinyZapnuto: !!found?.kvetinyZapnuto,
        kvetinyCastka,
        dekoraceZapnuto: !!found?.dekoraceZapnuto,
        dekoraceCastka,
      };
    });
  }, [form.balicekTerminy, form.balicekNavstevy]);

  useEffect(() => {
    if (!isBalicekSelected) return;

    const current = Array.isArray(form.balicekNavstevy) ? form.balicekNavstevy : [];
    if (JSON.stringify(current) !== JSON.stringify(balicekNavstevy)) {
      setForm((prev) => ({
        ...prev,
        balicekNavstevy,
      }));
    }
  }, [isBalicekSelected, balicekNavstevy]);

  function updateBalicekNavsteva(datum, key, value) {
    setForm((prev) => {
      const dates = (Array.isArray(prev.balicekTerminy) ? prev.balicekTerminy : [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      const current = Array.isArray(prev.balicekNavstevy) ? prev.balicekNavstevy : [];
      const normalized = dates.map((d, index) => {
        const found = current.find((x) => String(x?.datum || "").slice(0, 10) === d) || {};
        return {
          id: String(found?.id || `balicek-navsteva-${index + 1}`),
          datum: d,
          poradi: index + 1,
          jeZdarma: index >= 4,
          kvetinyZapnuto: !!found?.kvetinyZapnuto,
          kvetinyCastka: Math.round(Number(found?.kvetinyCastka || 0) * 100) / 100,
          dekoraceZapnuto: !!found?.dekoraceZapnuto,
          dekoraceCastka: Math.round(Number(found?.dekoraceCastka || 0) * 100) / 100,
        };
      });

      const next = normalized.map((item) => {
        if (item.datum !== datum) return item;

        const out = { ...item, [key]: value };

        if (key === "kvetinyZapnuto" && !value) out.kvetinyCastka = 0;
        if (key === "dekoraceZapnuto" && !value) out.dekoraceCastka = 0;

        if (key === "kvetinyCastka") {
          const castka = Math.max(0, Math.round(Number(value || 0) * 100) / 100);
          out.kvetinyCastka = castka;
          out.kvetinyZapnuto = castka > 0;
        }

        if (key === "dekoraceCastka") {
          const castka = Math.max(0, Math.round(Number(value || 0) * 100) / 100);
          out.dekoraceCastka = castka;
          out.dekoraceZapnuto = castka > 0;
        }

        return out;
      });

      return {
        ...prev,
        balicekNavstevy: next,
      };
    });
  }

  const previewBalicekKvetinyCelkem = useMemo(() => {
    if (!isBalicekSelected) return 0;
    return Math.round(
      balicekNavstevy.reduce((sum, item) => sum + (item.kvetinyZapnuto ? Number(item.kvetinyCastka || 0) : 0), 0) * 100
    ) / 100;
  }, [isBalicekSelected, balicekNavstevy]);

  const previewBalicekDekoraceCelkem = useMemo(() => {
    if (!isBalicekSelected) return 0;
    return Math.round(
      balicekNavstevy.reduce((sum, item) => sum + (item.dekoraceZapnuto ? Number(item.dekoraceCastka || 0) : 0), 0) * 100
    ) / 100;
  }, [isBalicekSelected, balicekNavstevy]);

  const selectedText = useMemo(() => {
    return Array.isArray(form.sluzby) && form.sluzby.length ? form.sluzby.join(", ") : "";
  }, [form.sluzby]);

  const selectedMainService = useMemo(() => {
    return getHlavniSluzbaById(form.hlavniSluzba || "");
  }, [form.hlavniSluzba]);

  const isObnovaSelected =
    String(form.hlavniSluzba || "").trim() === "obnova_hrobu";

  const isIndividualniSelected =
    String(form.hlavniSluzba || "").trim() === "individualni_poptavka";

  const orientacniZaklad = useMemo(() => {
    if (!form.hlavniSluzba || !form.typHrobu) return 0;
    return getZakladniCenu(form.hlavniSluzba, form.typHrobu);
  }, [form.hlavniSluzba, form.typHrobu]);

  const jednorazoveCisteniServiceId = useMemo(() => {
    const found = Array.isArray(HLAVNI_SLUZBY_PUBLIC)
      ? HLAVNI_SLUZBY_PUBLIC.find((item) =>
          String(item?.nazev || "").toLowerCase().includes("jednorázové čištění")
          || String(item?.nazev || "").toLowerCase().includes("jednorazove cisteni")
        )
      : null;
    return String(found?.id || "");
  }, []);

  const orientacniZakladZaNavstevu = useMemo(() => {
    if (!form.typHrobu) return 0;

    if (isBalicekSelected && jednorazoveCisteniServiceId) {
      return Number(getZakladniCenu(jednorazoveCisteniServiceId, form.typHrobu) || 0);
    }

    return Number(orientacniZaklad || 0);
  }, [form.typHrobu, isBalicekSelected, jednorazoveCisteniServiceId, orientacniZaklad]);

  const kvetinyCastka = useMemo(() => {
    if (form.kvetinyLimit === "vlastni") return Number(form.kvetinyVlastniCastka || 0);
    const map = {
      bez: 0,
      do_200: 200,
      do_300: 300,
      do_500: 500,
      do_800: 800,
    };
    return Number(map[form.kvetinyLimit || "bez"] || 0);
  }, [form.kvetinyLimit, form.kvetinyVlastniCastka]);

  const dekoraceCastka = useMemo(() => {
    if (form.dekoraceLimit === "vlastni") return Number(form.dekoraceVlastniCastka || 0);
    const map = {
      bez: 0,
      do_200: 200,
      do_300: 300,
      do_500: 500,
      do_800: 800,
    };
    return Number(map[form.dekoraceLimit || "bez"] || 0);
  }, [form.dekoraceLimit, form.dekoraceVlastniCastka]);

  const kvetinyVybraneNavstevy = useMemo(() => {
    return Array.isArray(form.kvetinyVybraneNavstevy) ? form.kvetinyVybraneNavstevy : [];
  }, [form.kvetinyVybraneNavstevy]);

  const dekoraceVybraneNavstevy = useMemo(() => {
    return Array.isArray(form.dekoraceVybraneNavstevy) ? form.dekoraceVybraneNavstevy : [];
  }, [form.dekoraceVybraneNavstevy]);

  const previewPredplatneCountRaw = useMemo(() => {
    if (!isPredplatneSelected || !form.datumOd) return 0;
    return form.periodicita === "mesic"
      ? 12
      : form.periodicita === "ctvrtletne"
        ? 4
        : form.periodicita === "pulrocne"
          ? 2
          : 1;
  }, [isPredplatneSelected, form.datumOd, form.periodicita]);

  const previewKvetinyPocet = useMemo(() => {
    if (!isPredplatneSelected) return Number(kvetinyCastka || 0) > 0 ? 1 : 0;
    if (!(Number(kvetinyCastka || 0) > 0)) return 0;
    return kvetinyVybraneNavstevy.length > 0
      ? kvetinyVybraneNavstevy.length
      : Number(previewPredplatneCountRaw || 0);
  }, [isPredplatneSelected, kvetinyCastka, kvetinyVybraneNavstevy, previewPredplatneCountRaw]);

  const previewDekoracePocet = useMemo(() => {
    if (!isPredplatneSelected) return Number(dekoraceCastka || 0) > 0 ? 1 : 0;
    if (!(Number(dekoraceCastka || 0) > 0)) return 0;
    return dekoraceVybraneNavstevy.length > 0
      ? dekoraceVybraneNavstevy.length
      : Number(previewPredplatneCountRaw || 0);
  }, [isPredplatneSelected, dekoraceCastka, dekoraceVybraneNavstevy, previewPredplatneCountRaw]);

  const orientacniCenaCelkem = useMemo(() => {
    return Number(orientacniZaklad || 0) + Number(kvetinyCastka || 0) + Number(dekoraceCastka || 0);
  }, [orientacniZaklad, kvetinyCastka, dekoraceCastka]);

  const predplatnePlanPreview = useMemo(() => {
    if (!isPredplatneSelected || !form.datumOd) return [];
    const out = [];
    const count =
      form.periodicita === "mesic"
        ? 12
        : form.periodicita === "ctvrtletne"
          ? 4
          : form.periodicita === "pulrocne"
            ? 2
            : 1;

    let current = new Date(`${String(form.datumOd).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(current.getTime())) return [];

    for (let i = 0; i < count; i += 1) {
      out.push(formatDateForInput(current));
      const next = new Date(current);
      if (form.periodicita === "mesic") next.setMonth(next.getMonth() + 1);
      else if (form.periodicita === "ctvrtletne") next.setMonth(next.getMonth() + 3);
      else if (form.periodicita === "pulrocne") next.setMonth(next.getMonth() + 6);
      else next.setFullYear(next.getFullYear() + 1);
      current = next;
    }
    return out;
  }, [isPredplatneSelected, form.datumOd, form.periodicita]);

  const previewPocetNavstev = useMemo(() => {
    if (isPredplatneSelected) return predplatnePlanPreview.length || 0;
    if (isBalicekSelected) return (form.balicekTerminy || []).filter(Boolean).length || 0;
    return orientacniZaklad > 0 ? 1 : 0;
  }, [isPredplatneSelected, predplatnePlanPreview, isBalicekSelected, form.balicekTerminy, orientacniZaklad]);

  const previewPocetPlacenychNavstev = useMemo(() => {
    if (isBalicekSelected) return Math.min(4, Number(previewPocetNavstev || 0));
    return Number(previewPocetNavstev || 0);
  }, [isBalicekSelected, previewPocetNavstev]);

  const previewCenaZaNavstevuBezDph = useMemo(() => {
    return Math.round(Number(orientacniZakladZaNavstevu || 0) * 100) / 100;
  }, [orientacniZakladZaNavstevu]);

  const previewCelkemBezDph = useMemo(() => {
    if (isPredplatneSelected) {
      const hlavni = Number(orientacniZakladZaNavstevu || 0) * Number(previewPocetNavstev || 0);
      const kvetiny = Number(kvetinyCastka || 0) * Number(previewKvetinyPocet || 0);
      const dekorace = Number(dekoraceCastka || 0) * Number(previewDekoracePocet || 0);
      return Math.round((hlavni + kvetiny + dekorace) * 100) / 100;
    }

    if (isBalicekSelected) {
      const hlavni = Number(orientacniZakladZaNavstevu || 0) * Number(previewPocetPlacenychNavstev || 0);
      const kvetiny = Number(previewBalicekKvetinyCelkem || 0);
      const dekorace = Number(previewBalicekDekoraceCelkem || 0);
      return Math.round((hlavni + kvetiny + dekorace) * 100) / 100;
    }

    return Math.round(
      (Number(orientacniZakladZaNavstevu || 0) + Number(kvetinyCastka || 0) + Number(dekoraceCastka || 0)) * 100
    ) / 100;
  }, [
    isPredplatneSelected,
    isBalicekSelected,
    orientacniZakladZaNavstevu,
    kvetinyCastka,
    dekoraceCastka,
    previewPocetNavstev,
    previewPocetPlacenychNavstev,
    previewKvetinyPocet,
    previewDekoracePocet,
    previewBalicekKvetinyCelkem,
    previewBalicekDekoraceCelkem,
  ]);

  const previewDph = useMemo(() => {
    return Math.round(previewCelkemBezDph * 0.21 * 100) / 100;
  }, [previewCelkemBezDph]);

  const previewCelkemSDph = useMemo(() => {
    return Math.round((previewCelkemBezDph + previewDph) * 100) / 100;
  }, [previewCelkemBezDph, previewDph]);

  const canShowNumericPreview =
    !!selectedMainService &&
    !isObnovaSelected &&
    !isIndividualniSelected &&
    !!String(form.typHrobu || "").trim() &&
    Number(orientacniZaklad || 0) > 0;

  useEffect(() => {
    if (!isBalicekSelected) return;
    const filled = (form.balicekTerminy || []).filter(Boolean).length;
    const recommended = filled >= 4 ? Math.min(6, filled + 1) : 4;
    setBalicekVisibleCount((prev) => Math.min(6, Math.max(prev, recommended)));
  }, [isBalicekSelected, form.balicekTerminy]);

  function addBalicekDatumField() {
    setBalicekVisibleCount((prev) => Math.min(6, prev + 1));
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const applyBalicekVisualSelection = (prevForm, kind, value) => {
    const isKvetiny = kind === "kvetiny";
    const cenikId = isKvetiny ? "__balicek_kvetiny__" : "__balicek_dekorace__";

    const castkaMap = isKvetiny
      ? {
          bez: 0,
          do_200: 200,
          do_300: 300,
          do_500: 500,
          vlastni: Number(prevForm.kvetinyVlastniCastka || 0),
        }
      : {
          bez: 0,
          do_100: 100,
          do_200: 200,
          do_300: 300,
          vlastni: Number(prevForm.dekoraceVlastniCastka || 0),
        };

    const labelMap = isKvetiny
      ? {
          bez: "",
          do_300: "Květiny do 300 Kč v rámci balíčku",
          do_500: "Květiny do 500 Kč v rámci balíčku",
          vlastni: "Květiny – vlastní částka v rámci balíčku",
        }
      : {
          bez: "",
          do_200: "Dekorace do 200 Kč v rámci balíčku",
          do_300: "Dekorace do 300 Kč v rámci balíčku",
          vlastni: "Dekorace – vlastní částka v rámci balíčku",
        };

    const nextLimit = String(value || "bez");
    const nextCastka = Number(castkaMap[nextLimit] || 0);

    const nextItems = (Array.isArray(prevForm.items) ? prevForm.items : []).filter(
      (x) => String(x?.cenikId || "") !== cenikId
    );

    if (nextLimit !== "bez" && String(labelMap[nextLimit] || "").trim()) {
      nextItems.push({
        id: `${cenikId}_${Date.now()}`,
        cenikId,
        nazev: String(labelMap[nextLimit] || "").trim(),
        skupina: "Balíček – limity péče",
        jednotka: "balíček",
        mnozstvi: 1,
        cenaBezDph: nextCastka,
        dph: 21,
        rezimUctovani: "jednou",
        vybraneNavstevy: [],
        jeBalicekVizual: true,
      });
    }

    return {
      ...prevForm,
      [isKvetiny ? "kvetinyLimit" : "dekoraceLimit"]: nextLimit,
      items: nextItems,
    };
  };

  function updateMainService(serviceId) {
    const found = getHlavniSluzbaById(serviceId);
    const serviceName = found?.nazev || "";

    setForm((prev) => {
      const nextSluzby = serviceName ? [serviceName] : [];
      const next = {
        ...prev,
        hlavniSluzba: serviceId,
        sluzby: nextSluzby,
      };

      if (serviceId === "pravidelna_pece" && !String(prev.datumOd || "").trim()) {
        next.datumOd = new Date().toISOString().slice(0, 10);
      }

      if (!serviceName || !serviceName.includes("Základní péče o hrob")) {
        next.balicekTerminy = ["", "", "", "", "", ""];
        next.vyznamneDny = [];
      }

      return next;
    });
  }

  function toggleService(name) {
    setForm((prev) => {
      const nextSluzby = prev.sluzby.includes(name)
        ? prev.sluzby.filter((x) => x !== name)
        : [...prev.sluzby, name];

      const next = { ...prev, sluzby: nextSluzby };

      if (!nextSluzby.includes("Základní péče o hrob – 4 návštěvy + 5. zdarma")) {
        next.balicekTerminy = ["", "", "", "", "", ""];
        next.vyznamneDny = [];
      }

      return next;
    });
  }

  function updateBalicekTermin(index, value) {
    setForm((prev) => {
      const selectedDates = getSelectedImportantDates(prev.vyznamneDny, vyznamneDnyOptions);
      const current = [...(prev.balicekTerminy || ["", "", "", "", "", ""])];
      current[index] = value;

      const manualDates = [];
      for (const d of current) {
        const x = String(d || "").trim();
        if (!x) continue;
        if (selectedDates.includes(x)) continue;
        if (!manualDates.includes(x)) manualDates.push(x);
      }

      const merged = [...selectedDates];
      for (const d of manualDates) {
        if (merged.length >= 5) break;
        if (!merged.includes(d)) merged.push(d);
      }

      while (merged.length < 6) merged.push("");

      return {
        ...prev,
        balicekTerminy: merged,
      };
    });
  }

  function toggleVyznamnyDen(key) {
    setForm((prev) => {
      const current = Array.isArray(prev.vyznamneDny) ? prev.vyznamneDny : [];
      let nextKeys = current.includes(key)
        ? current.filter((x) => x !== key)
        : [...current, key];

      const dated = nextKeys
        .map((k) => {
          const found = vyznamneDnyOptions.find((x) => x.key === k);
          return {
            key: k,
            datum: String(found?.datum || "").trim(),
          };
        })
        .filter((x) => x.datum)
        .sort((a, b) => a.datum.localeCompare(b.datum))
        .slice(0, 6);

      nextKeys = dated.map((x) => x.key);

      const selectedDates = dated.map((x) => x.datum);
      const blocked = new Set(getAllImportantDates(vyznamneDnyOptions));
      const currentDates = Array.isArray(prev.balicekTerminy) ? prev.balicekTerminy : ["", "", "", "", "", ""];

      const manualDates = [];
      for (const d of currentDates) {
        const x = String(d || "").trim();
        if (!x) continue;
        if (selectedDates.includes(x)) continue;
        if (blocked.has(x)) continue;
        if (!manualDates.includes(x)) manualDates.push(x);
      }

      const merged = [...selectedDates];
      for (const d of manualDates) {
        if (merged.length >= 6) break;
        if (!merged.includes(d)) merged.push(d);
      }

      while (merged.length < 6) merged.push("");

      return {
        ...prev,
        vyznamneDny: nextKeys,
        balicekTerminy: merged,
      };
    });
  }

  function odebratRucniTermin(datum) {
    setForm((prev) => {
      const selectedDates = getSelectedImportantDates(prev.vyznamneDny, vyznamneDnyOptions);
      const current = Array.isArray(prev.balicekTerminy) ? prev.balicekTerminy : ["", "", "", "", "", ""];

      const manualDates = [];
      for (const d of current) {
        const x = String(d || "").trim();
        if (!x) continue;
        if (x === datum) continue;
        if (selectedDates.includes(x)) continue;
        if (allImportantDates.includes(x)) continue;
        if (!manualDates.includes(x)) manualDates.push(x);
      }

      const merged = [...selectedDates];
      for (const d of manualDates) {
        if (merged.length >= 6) break;
        if (!merged.includes(d)) merged.push(d);
      }

      while (merged.length < 6) merged.push("");

      return {
        ...prev,
        balicekTerminy: merged,
      };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const errors = validatePublicZakazka(form);

    if (isBalicekSelected) {
      const validDates = (form.balicekTerminy || []).filter(Boolean);
      if (validDates.length < 1 || validDates.length > 6) {
        errors.push("U základní péči vyplňte 1 až 6 termínů návštěv.");
      }
    }
      if (isPredplatneSelected) {
        if (!String(form.periodicita || "").trim()) {
          errors.push("U pravidelné péče vyberte periodicitu.");
        }
        if (!String(form.datumOd || "").trim()) {
          errors.push("U pravidelné péče vyplňte datum od.");
        }
      }


    if (errors.length) {
      setDone({ ok: false, text: errors[0] });
      return;
    }

    setSending(true);
    setDone(null);

    try {
      const payload = normalizePublicZakazkaPayload({
          ...form,
          typPece: isBalicekSelected ? "balicek" : (isPredplatneSelected ? "predplatne" : "jednorazova"),
          orientacniCenaBezDph: Number(orientacniZakladZaNavstevu || 0),
          periodicita: isPredplatneSelected ? String(form.periodicita || "rocne") : "",
          datumOd: isPredplatneSelected ? String(form.datumOd || "").slice(0, 10) : "",
          pocetNavstevCelkem: isPredplatneSelected
            ? Number(predplatnePlanPreview.length || 0)
            : (
                isBalicekSelected
                  ? Number(
                      (Array.isArray(form.vyznamneDny) && form.vyznamneDny.length
                        ? form.vyznamneDny.length
                        : (Array.isArray(form.balicekTerminy)
                            ? form.balicekTerminy.filter(Boolean).length
                            : 0)
                      ) || 0
                    )
                  : 1
              ),
          planNavstev: isPredplatneSelected ? predplatnePlanPreview : [],
        kvetinyLimit: String(form.kvetinyLimit || "bez"),
        dekoraceLimit: String(form.dekoraceLimit || "bez"),
        kvetinyCastka: Number(kvetinyCastka || 0),
        dekoraceCastka: Number(dekoraceCastka || 0),
        kvetinyVlastniCastka:
          String(form.kvetinyLimit || "") === "vlastni"
            ? Number(form.kvetinyVlastniCastka || 0)
            : 0,
        dekoraceVlastniCastka:
          String(form.dekoraceLimit || "") === "vlastni"
            ? Number(form.dekoraceVlastniCastka || 0)
            : 0,
        balicekTerminy: form.balicekTerminy || [],
        balicekNavstevy: balicekNavstevy,
        vyznamneDny: (form.vyznamneDny || []).map((key) => {
          const found = vyznamneDnyOptions.find((x) => x.key === key);
          return found
            ? { key: found.key, label: found.label, datum: found.datum }
            : null;
        }).filter(Boolean),
      });

      const d = await apiJson(`${API}/verejna-objednavka`, {
        method: "POST",
        body: payload,
      });

      if (!d?.ok) {
        setDone({ ok: false, text: d?.error || "Odeslání objednávky selhalo." });
      } else {
        setDone({
          ok: true,
          text: `Děkujeme, objednávka byla odeslána. Evidenční číslo: ${d?.item?.id || "-"}`,
        });
        setForm(
          getEmptyZakazkaForm({
            zesnulyDatumNarozeni: "",
            balicekTerminy: ["", "", "", "", "", ""],
            vyznamneDny: [],
          })
        );
      }
    } catch (err) {
      setDone({ ok: false, text: err?.message || "Odeslání objednávky selhalo." });
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #eef4ff 0%, #f8fafc 18%, #f8fafc 100%)",
        padding: 16,
      }}
    >
      <div
        style={{
          ...cardStyle,
          maxWidth: 1100,
          margin: "0 auto",
          background: "rgba(255,255,255,0.94)",
          border: "1px solid #dbe7f5",
          borderRadius: 24,
          boxShadow: "0 20px 60px rgba(15,23,42,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            margin: -24,
            marginBottom: 24,
            padding: "42px 24px 28px",
            background: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 35%, #ffffff 100%)",
            borderBottom: "1px solid #dbeafe",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 999,
              background: "#ffffff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            Údržba hrobů • Český Krumlov a okolí
          </div>

          <h1
            style={{
              marginTop: 0,
              marginBottom: 10,
              fontSize: "clamp(34px, 6vw, 52px)",
              lineHeight: 1.05,
              color: "#0f172a",
              letterSpacing: "-0.03em",
            }}
          >
            Údržba hrobu bez starostí
          </h1>

          <div
            style={{
              color: "#334155",
              fontSize: 18,
              lineHeight: 1.7,
              maxWidth: 860,
              marginBottom: 18,
            }}
          >
            Pravidelná i jednorázová péče o hrobové místo, když tam nemůžete být osobně.
            Vyplňte formulář a připravím nezávazný návrh péče i orientační cenu.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
              marginBottom: 20,
            }}
          >
            {[
              "Pravidelná i jednorázová péče",
              "Návštěvy ve významných dnech",
              "Květiny, dekorace i drobné práce",
              "Individuální domluva podle přání rodiny",
            ].map((text) => (
              <div
                key={text}
                style={{
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: "rgba(255,255,255,0.88)",
                  border: "1px solid #dbeafe",
                  color: "#0f172a",
                  fontWeight: 700,
                  boxShadow: "0 8px 22px rgba(37,99,235,0.06)",
                }}
              >
                {text}
              </div>
            ))}
          </div>

          <div
            style={{
              padding: 20,
              borderRadius: 20,
              border: "1px solid #bfdbfe",
              background: "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.96) 100%)",
              boxShadow: "0 14px 34px rgba(15,23,42,0.06)",
              maxWidth: 920,
            }}
          >
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
              Péče o hrob, když tam nemůžete být osobně
            </div>

            <div style={{ color: "#334155", lineHeight: 1.8, fontSize: 16, marginBottom: 12 }}>
              Služba je vhodná pro rodiny, které chtějí mít hrob svých blízkých upravený,
              čistý a důstojný, ale nemohou se o něj starat samy tak často, jak by si přály.
            </div>

            <div style={{ color: "#334155", lineHeight: 1.8, fontSize: 16, marginBottom: 12 }}>
              Postarám se o běžný úklid, očištění hrobu, zapálení svíčky, zalití květin,
              drobnou dekoraci i návštěvy ve významných dnech. Vše podle domluvy a s ohledem
              na to, co konkrétní místo potřebuje.
            </div>

            <div style={{ color: "#0f172a", lineHeight: 1.8, fontSize: 16, fontWeight: 700 }}>
              Vyplňte formulář níže a po odeslání připravím nezávazný návrh péče a orientační cenu.
            </div>
          </div>
        </div>


        <UdrzbaGallerySection />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 26,
          }}
        >
          {[
            {
              title: "Osobní přístup",
              text: "Každý hrob i každá rodina mají jiné potřeby. Péči je možné nastavit individuálně.",
            },
            {
              title: "Pravidelná péče",
              text: "Možnost jednorázové i opakované údržby podle domluvy a zvolených termínů.",
            },
            {
              title: "Významné dny",
              text: "Návštěvy lze naplánovat na výročí, svátky, narozeniny, dušičky i další důležité dny.",
            },
            {
              title: "Přehledná domluva",
              text: "Vše si předem nastavíte ve formuláři, včetně termínů, květin, dekorací i poznámky.",
            },
          ].map((item) => (
            <div
              key={item.title}
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 18,
                padding: 18,
                boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  color: "#0f172a",
                  marginBottom: 8,
                  fontSize: 18,
                }}
              >
                {item.title}
              </div>
              <div
                style={{
                  color: "#475569",
                  fontSize: 14,
                  lineHeight: 1.7,
                }}
              >
                {item.text}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginBottom: 28,
            padding: 22,
            borderRadius: 20,
            background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
            border: "1px solid #dbe7f5",
            boxShadow: "0 10px 30px rgba(15,23,42,0.04)",
          }}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 14,
            }}
          >
            Jak to funguje
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 14,
            }}
          >
            {[
              {
                c: "1",
                t: "Vyplníte formulář",
                d: "Zadáte údaje o hrobu, vyberete typ péče, termíny návštěv a případné doplňky.",
              },
              {
                c: "2",
                t: "Připravím návrh péče",
                d: "Podle zadaných údajů připravím rozsah péče tak, aby odpovídal vašemu přání i stavu hrobu.",
              },
              {
                c: "3",
                t: "Potvrdíme domluvu",
                d: "Po upřesnění péče zajistím domluvené návštěvy a další potřebné práce.",
              },
            ].map((item) => (
              <div
                key={item.c}
                style={{
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 18,
                  padding: 18,
                  boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    marginBottom: 12,
                    boxShadow: "0 8px 18px rgba(37,99,235,0.25)",
                  }}
                >
                  {item.c}
                </div>

                <div
                  style={{
                    fontWeight: 800,
                    color: "#0f172a",
                    marginBottom: 8,
                    fontSize: 18,
                  }}
                >
                  {item.t}
                </div>

                <div
                  style={{
                    color: "#475569",
                    fontSize: 14,
                    lineHeight: 1.7,
                  }}
                >
                  {item.d}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            marginBottom: 18,
            padding: "16px 18px",
            borderRadius: 16,
            background: "#0f172a",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(15,23,42,0.18)",
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>
            Nezávazná poptávka péče o hrob
          </div>
          <div style={{ color: "rgba(255,255,255,0.82)", lineHeight: 1.7 }}>
            Vyplňte co nejvíce údajů. Když neznáte přesné číslo hrobu, pomohou i údaje pro dohledání.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={sectionBoxStyle}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
              Kontaktní údaje a místo hrobu
            </div>
            <div style={{ color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
              Vyplňte základní kontakt a údaje, podle kterých hrob bezpečně dohledám.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 12,
                alignItems: "start",
              }}
            >
            <input
              style={inputStyle}
              placeholder="Jméno a příjmení"
              value={form.objednavatel}
              onChange={(e) => updateField("objednavatel", e.target.value)}
            />

            <input
              style={inputStyle}
              placeholder="Telefon"
              value={form.telefon}
              onChange={(e) => updateField("telefon", e.target.value)}
            />

            <input
              style={inputStyle}
              type="email"
              placeholder="E-mail"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
            />

            <input
              style={inputStyle}
              placeholder="Hřbitov"
              value={form.hrbitov}
              onChange={(e) => updateField("hrbitov", e.target.value)}
            />

            <input
              style={inputStyle}
              placeholder="Číslo hrobu"
              value={form.cisloHrobu}
              onChange={(e) => updateField("cisloHrobu", e.target.value)}
            />

            <input
              style={inputStyle}
              type="date"
              value={form.planovaneDatum}
              onChange={(e) => updateField("planovaneDatum", e.target.value)}
            />

            <div
              style={{
                gridColumn: "1 / -1",
                marginTop: 4,
                padding: 14,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#f8fafc",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Upřesnění hrobu / dohledání</div>
              <div style={{ color: "#64748b", fontSize: 13, marginBottom: 10 }}>
                Když neznáte přesné číslo hrobu, vyplňte aspoň něco z údajů níže.
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 10,
                }}
              >
                <input
                  style={inputStyle}
                  value={form.oddeleni || ""}
                  onChange={(e) => updateField("oddeleni", e.target.value)}
                  placeholder="Oddělení"
                />
                <input
                  style={inputStyle}
                  value={form.skupina || ""}
                  onChange={(e) => updateField("skupina", e.target.value)}
                  placeholder="Skupina"
                />
                <input
                  style={inputStyle}
                  value={form.majitelHrobuJmeno || ""}
                  onChange={(e) => updateField("majitelHrobuJmeno", e.target.value)}
                  placeholder="Jméno a příjmení majitele hrobu"
                />
                <input
                  style={inputStyle}
                  value={form.zesnulyPrijmeni || ""}
                  onChange={(e) => updateField("zesnulyPrijmeni", e.target.value)}
                  placeholder="Příjmení zesnulého"
                />
                <input
                  style={inputStyle}
                  value={form.zesnulyDatumUmrti || ""}
                  onChange={(e) => updateField("zesnulyDatumUmrti", e.target.value)}
                  placeholder="Datum úmrtí (rrrr-mm-dd)"
                />
                <input
                  style={inputStyle}
                  value={form.zesnulyDatumNarozeni || ""}
                  onChange={(e) => updateField("zesnulyDatumNarozeni", e.target.value)}
                  placeholder="Datum narození (rrrr-mm-dd)"
                />
                <input
                  style={inputStyle}
                  value={form.jmenoNaDesce || ""}
                  onChange={(e) => updateField("jmenoNaDesce", e.target.value)}
                  placeholder="Jméno na nápisové desce"
                />
              </div>
            </div>
          </div>
          </div>

            <div style={sectionBoxStyle}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
                Typ hrobu
              </div>
              <div style={{ color: "#64748b", marginBottom: 14, lineHeight: 1.6 }}>
                Vyberte, o jaký typ hrobu se jedná. Podle toho se zobrazí správný orientační základ ceny.
              </div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Typ hrobu</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
                {HROBY_PUBLIC.map((item) => (
                  <label
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      border: form.typHrobu === item.id ? "1px solid #93c5fd" : "1px solid #dbe7f5",
                      borderRadius: 16,
                      padding: 14,
                      background: form.typHrobu === item.id
                        ? "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)"
                        : "#ffffff",
                      boxShadow: form.typHrobu === item.id
                        ? "0 10px 24px rgba(37,99,235,0.10)"
                        : "0 4px 14px rgba(15,23,42,0.04)",
                    }}
                  >
                    <input
                      type="radio"
                      name="typHrobu"
                      checked={form.typHrobu === item.id}
                      onChange={() => updateField("typHrobu", item.id)}
                    />
                    <span>{item.nazev}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={sectionBoxStyle}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
                Výběr služby
              </div>
              <div style={{ color: "#64748b", marginBottom: 14, lineHeight: 1.6 }}>
                Zvolte rozsah péče. U pravidelných nebo balíčkových služeb si následně nastavíte konkrétní termíny návštěv.
              </div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Hlavní služba</div>
              <div style={{ display: "grid", gap: 10 }}>
                {HLAVNI_SLUZBY_PUBLIC.map((item) => {
                  const zaklad = form.typHrobu ? getZakladniCenu(item.id, form.typHrobu) : 0;
                  return (
                    <label
                      key={item.id}
                      style={{
                        display: "block",
                        border: form.hlavniSluzba === item.id ? "1px solid #93c5fd" : "1px solid #dbe7f5",
                        borderRadius: 20,
                        padding: 18,
                        background: form.hlavniSluzba === item.id
                          ? "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)"
                          : "#ffffff",
                        cursor: "pointer",
                        boxShadow: form.hlavniSluzba === item.id
                          ? "0 14px 30px rgba(37,99,235,0.12)"
                          : "0 6px 18px rgba(15,23,42,0.04)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <input
                          type="radio"
                          name="hlavniSluzba"
                          checked={form.hlavniSluzba === item.id}
                          onChange={() => updateMainService(item.id)}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>{item.nazev}</div>
                          <div style={{ color: "#4b5563", fontSize: 14, marginTop: 4 }}>
                            {item.popis}
                          </div>
                          {Array.isArray(item.obsah) && item.obsah.length ? (
                            <div style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
                              Obsah: {item.obsah.join(", ")}
                            </div>
                          ) : null}
                          <div style={{ color: "#0f172a", fontWeight: 700, marginTop: 8 }}>
                            {(item.id === "obnova_hrobu" || item.id === "individualni_poptavka")
                              ? "Cena bude upřesněna"
                              : (form.typHrobu
                                  ? (zaklad > 0
                                      ? `Orientační základ: ${formatCena(zaklad)} bez DPH`
                                      : "Cena bude upřesněna")
                                  : "Nejprve vyberte typ hrobu")}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={sectionBoxStyle}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
                Květiny a dekorace
              </div>
              <div style={{ color: "#64748b", marginBottom: 14, lineHeight: 1.6 }}>
                Můžete nechat hrob doplnit i o květiny nebo dekoraci. U balíčku se výběr přiřazuje ke konkrétním návštěvám.
              </div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Květiny a dekorace</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: 12,
                }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Výsadba květin</div>
                  <select
                    style={inputStyle}
                    value={form.kvetinyLimit || "bez"}
                    onChange={(e) => setForm((prev) => applyBalicekVisualSelection(prev, "kvetiny", e.target.value))}
                  >
                    {LIMITY_KVETINY.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>

                  {form.kvetinyLimit === "vlastni" ? (
                    <input
                      style={{ ...inputStyle, marginTop: 8 }}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Vlastní částka za květiny v Kč"
                      value={form.kvetinyVlastniCastka || ""}
                      onChange={(e) => {
                          const value = e.target.value;
                          setForm((prev) => {
                            const next = { ...prev, kvetinyVlastniCastka: value };
                            return String(prev.kvetinyLimit || "") === "vlastni"
                              ? applyBalicekVisualSelection(next, "kvetiny", "vlastni")
                              : next;
                          });
                        }}
                    />
                  ) : null}
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Dekorace na hrob</div>
                  <select
                    style={inputStyle}
                    value={form.dekoraceLimit || "bez"}
                    onChange={(e) => setForm((prev) => applyBalicekVisualSelection(prev, "dekorace", e.target.value))}
                  >
                    {LIMITY_DEKORACE.map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>

                  {form.dekoraceLimit === "vlastni" ? (
                    <input
                      style={{ ...inputStyle, marginTop: 8 }}
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Vlastní částka za dekorace v Kč"
                      value={form.dekoraceVlastniCastka || ""}
                      onChange={(e) => {
                          const value = e.target.value;
                          setForm((prev) => {
                            const next = { ...prev, dekoraceVlastniCastka: value };
                            return String(prev.dekoraceLimit || "") === "vlastni"
                              ? applyBalicekVisualSelection(next, "dekorace", "vlastni")
                              : next;
                          });
                        }}
                    />
                  ) : null}
                </div>
              </div>
            </div>

            {orientacniZaklad > 0 ? (
              <div
                style={{
                  marginTop: 18,
                  padding: 14,
                  border: "1px solid #dbeafe",
                  borderRadius: 12,
                  background: "#eff6ff",
                }}
              >
                <div style={{ fontWeight: 800, color: "#1d4ed8" }}>
                  Orientační základ ceny: {formatCena(orientacniZaklad)} bez DPH
                </div>
                <div style={{ color: "#475569", fontSize: 13, marginTop: 6 }}>
                  Jde o základ hlavní služby podle typu hrobu. Květiny, dekorace, mytí desky a další doplňky doplníme v dalším kroku podle jednotlivých termínů.
                </div>
              </div>
            ) : null}



          {isPredplatneSelected ? (

            <div

              style={{

                marginTop: 18,

                padding: 14,

                border: "1px solid #e5e7eb",

                borderRadius: 12,

                background: "#f8fafc",

              }}

            >

              <div style={{ fontWeight: 700, marginBottom: 8 }}>Nastavení pravidelné péče</div>


              <div

                style={{

                  display: "grid",

                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",

                  gap: 12,

                  marginBottom: 14,

                }}

              >

                <div>

                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Periodicita</label>

                  <select

                    style={inputStyle}

                    value={form.periodicita || "rocne"}

                    onChange={(e) => updateField("periodicita", e.target.value)}

                  >

                    <option value="mesic">Měsíčně</option>

                    <option value="ctvrtletne">Čtvrtletně</option>

                    <option value="pulrocne">Půlročně</option>

                    <option value="rocne">Ročně</option>

                  </select>

                </div>


                <div>

                  <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Datum od</label>

                  <input

                    type="date"

                    style={inputStyle}

                    value={form.datumOd || ""}

                    onChange={(e) => updateField("datumOd", e.target.value)}

                  />

                </div>

              </div>


              {predplatnePlanPreview.length ? (

                <>

                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Náhled termínů návštěv</div>

                  <div style={{ display: "grid", gap: 8 }}>

                    {predplatnePlanPreview.map((d, i) => (

                      <div

                        key={d + i}

                        style={{

                          border: "1px solid #e5e7eb",

                          borderRadius: 10,

                          padding: 10,

                          background: "#fff",

                        }}

                      >

                        Návštěva {i + 1}: {formatCzDate(d)}

                      </div>

                    ))}

                  </div>

                </>

              ) : null}

                {kvetinyCastka > 0 && predplatnePlanPreview.length ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Vybrat návštěvy pro květiny</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {predplatnePlanPreview.map((d, i) => {
                        const checked = (Array.isArray(form.kvetinyVybraneNavstevy) ? form.kvetinyVybraneNavstevy : []).includes(d);
                        return (
                          <label
                            key={`kvetiny-${d}-${i}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              border: '1px solid #e5e7eb',
                              borderRadius: 10,
                              padding: 10,
                              background: checked ? '#ecfdf5' : '#fff',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = Array.isArray(form.kvetinyVybraneNavstevy) ? form.kvetinyVybraneNavstevy : [];
                                if (e.target.checked) updateField('kvetinyVybraneNavstevy', [...current, d]);
                                else updateField('kvetinyVybraneNavstevy', current.filter((x) => x !== d));
                              }}
                            />
                            <span>Návštěva {i + 1}: {formatCzDate(d)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {dekoraceCastka > 0 && predplatnePlanPreview.length ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Vybrat návštěvy pro dekorace</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {predplatnePlanPreview.map((d, i) => {
                        const checked = (Array.isArray(form.dekoraceVybraneNavstevy) ? form.dekoraceVybraneNavstevy : []).includes(d);
                        return (
                          <label
                            key={`dekorace-${d}-${i}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              border: '1px solid #e5e7eb',
                              borderRadius: 10,
                              padding: 10,
                              background: checked ? '#eff6ff' : '#fff',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = Array.isArray(form.dekoraceVybraneNavstevy) ? form.dekoraceVybraneNavstevy : [];
                                if (e.target.checked) updateField('dekoraceVybraneNavstevy', [...current, d]);
                                else updateField('dekoraceVybraneNavstevy', current.filter((x) => x !== d));
                              }}
                            />
                            <span>Návštěva {i + 1}: {formatCzDate(d)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

            </div>

          ) : null}



          {isBalicekSelected ? (
            <div
              style={{
                marginTop: 18,
                padding: 14,
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                background: "#f8fafc",
              }}
            >
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Základní péče – výběr termínů</div>
              <div style={{ color: "#64748b", fontSize: 13, marginBottom: 12 }}>
                  Balíček umožňuje až 6 termínů návštěv. První 4 jsou účtované, 5. a 6. návštěva jsou zdarma. Můžete ručně vyplnit termíny a/nebo vybrat významné dny s přesným datem. Výročí úmrtí, narození i jmeniny se zobrazí až po vyplnění příslušných údajů.
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                {Array.from({ length: balicekVisibleCount }, (_, index) => (
                  <input
                    key={index}
                    style={inputStyle}
                    type="date"
                    value={(form.balicekTerminy || ["", "", "", "", "", ""])[index] || ""}
                    onChange={(e) => updateBalicekTermin(index, e.target.value)}
                  />
                ))}
              </div>

              {balicekVisibleCount < 6 && (
                <div style={{ marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={addBalicekDatumField}
                    style={{
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      borderRadius: 10,
                      padding: "10px 14px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    + Přidat datum
                  </button>
                </div>
              )}

              {balicekNavstevy.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Doplňky ke konkrétním návštěvám</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {balicekNavstevy.map((navsteva) => (
                      <div
                        key={`balicek-navsteva-row-${navsteva.datum}`}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          padding: 12,
                          background: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                          <div style={{ fontWeight: 700 }}>
                            Návštěva {navsteva.poradi}: {formatCzDate(navsteva.datum)}
                          </div>
                          <div style={{ color: navsteva.jeZdarma ? "#166534" : "#475569", fontWeight: 700 }}>
                            {navsteva.jeZdarma ? "Hlavní služba zdarma" : "Hlavní služba účtována"}
                          </div>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                          <label
                            style={{
                              display: "grid",
                              gap: 8,
                              border: "1px solid #e5e7eb",
                              borderRadius: 10,
                              padding: 10,
                              background: navsteva.kvetinyZapnuto ? "#ecfdf5" : "#fff",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                              <input
                                type="checkbox"
                                checked={!!navsteva.kvetinyZapnuto}
                                onChange={(e) => updateBalicekNavsteva(navsteva.datum, "kvetinyZapnuto", e.target.checked)}
                              />
                              Květiny pro tuto návštěvu
                            </span>
                            <input
                              style={inputStyle}
                              type="number"
                              min="0"
                              step="1"
                              placeholder="Částka za květiny v Kč"
                              value={navsteva.kvetinyZapnuto ? (navsteva.kvetinyCastka || "") : ""}
                              disabled={!navsteva.kvetinyZapnuto}
                              onChange={(e) => updateBalicekNavsteva(navsteva.datum, "kvetinyCastka", e.target.value)}
                            />
                          </label>

                          <label
                            style={{
                              display: "grid",
                              gap: 8,
                              border: "1px solid #e5e7eb",
                              borderRadius: 10,
                              padding: 10,
                              background: navsteva.dekoraceZapnuto ? "#eff6ff" : "#fff",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                              <input
                                type="checkbox"
                                checked={!!navsteva.dekoraceZapnuto}
                                onChange={(e) => updateBalicekNavsteva(navsteva.datum, "dekoraceZapnuto", e.target.checked)}
                              />
                              Dekorace pro tuto návštěvu
                            </span>
                            <input
                              style={inputStyle}
                              type="number"
                              min="0"
                              step="1"
                              placeholder="Částka za dekorace v Kč"
                              value={navsteva.dekoraceZapnuto ? (navsteva.dekoraceCastka || "") : ""}
                              disabled={!navsteva.dekoraceZapnuto}
                              onChange={(e) => updateBalicekNavsteva(navsteva.datum, "dekoraceCastka", e.target.value)}
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ fontWeight: 700, marginBottom: 8 }}>Významné dny</div>
              <div style={{ display: "grid", gap: 8 }}>
                {vyznamneDnyOptions.map((item) => (
                  <label
                    key={item.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 10,
                      background: "#fff",
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={(form.vyznamneDny || []).includes(item.key)}
                        disabled={!(form.vyznamneDny || []).includes(item.key) && (form.vyznamneDny || []).length >= 6}
                        onChange={() => toggleVyznamnyDen(item.key)}
                      />
                      <span>{item.label}</span>
                    </span>
                    <span style={{ color: "#475569", fontWeight: 600 }}>
                      {formatCzDate(item.datum)}
                    </span>
                  </label>
                ))}

                {rucniTerminyOptions.length > 0 && (
                  <>
                    <div style={{ fontWeight: 700, marginTop: 10 }}>Ručně přidané termíny</div>
                    {rucniTerminyOptions.map((datum) => (
                      <label
                        key={`manual-${datum}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          padding: 10,
                          background: "#fff",
                        }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox"
                            checked
                            onChange={(e) => {
                              if (!e.target.checked) odebratRucniTermin(datum);
                            }}
                          />
                          <span>Ruční termín</span>
                        </span>
                        <span style={{ color: "#475569", fontWeight: 600 }}>
                          {formatCzDate(datum)}
                        </span>
                      </label>
                    ))}
                  </>
                )}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 18 }}>
            <textarea
              style={{ ...inputStyle, minHeight: 120 }}
              placeholder="Poznámka k objednávce"
              value={form.poznamka}
              onChange={(e) => updateField("poznamka", e.target.value)}
            />
          </div>

          <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
            Vybrané služby: {selectedText || "neuvedeno"}
          </div>



          <div style={sectionBoxStyle}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
              Odeslání objednávky
            </div>
            <div style={{ color: "#64748b", marginBottom: 16, lineHeight: 1.6 }}>
              Po odeslání objednávku zkontroluji, připravím návrh péče a ozvu se vám s potvrzením rozsahu i ceny.
            </div>

            <div style={{ marginTop: 8, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={sending}
              style={{
                padding: "16px 24px",
                borderRadius: 18,
                border: "1px solid #2563eb",
                background: sending
                  ? "#93c5fd"
                  : "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                color: "#fff",
                fontWeight: 800,
                fontSize: 17,
                cursor: sending ? "default" : "pointer",
                boxShadow: sending ? "none" : "0 14px 30px rgba(37,99,235,0.22)",
              }}
            >
              {sending ? "Odesílám..." : "Odeslat objednávku"}
            </button>

            {done ? (
              <div style={{ color: done.ok ? "#166534" : "#991b1b", fontWeight: 600 }}>
                {done.text}
              </div>
            ) : null}
          </div>
          {(selectedMainService || selectedText) && (
            <div
              style={{
                marginTop: 18,
                padding: 20,
                border: "1px solid #dbe7f5",
                borderRadius: 20,
                background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                boxShadow: "0 12px 30px rgba(15,23,42,0.05)",
              }}
            >
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Orientační kalkulace</div>

              {canShowNumericPreview ? (
                <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
                  <div>Základ služby bez DPH: <strong>{formatCena(Number(orientacniZakladZaNavstevu || 0))}</strong></div>
                  {isPredplatneSelected && (
                    <div>Počet návštěv: <strong>{previewPocetNavstev}</strong></div>
                  )}
                  {isBalicekSelected && (
                    <>
                      <div>Počet termínů: <strong>{(form.balicekTerminy || []).filter(Boolean).length}</strong></div>
                      <div>Účtované návštěvy: <strong>{previewPocetPlacenychNavstev}</strong> <span style={{ color: "#475569" }}>(5. návštěva zdarma)</span></div>
                    </>
                  )}
                  <div>Květiny: <strong>{formatCena(
                    isBalicekSelected
                      ? Number(previewBalicekKvetinyCelkem || 0)
                      : (isPredplatneSelected
                          ? (Number(kvetinyCastka || 0) * Number(previewKvetinyPocet || 0))
                          : Number(kvetinyCastka || 0))
                  )}</strong></div>
                  <div>Dekorace: <strong>{formatCena(
                    isBalicekSelected
                      ? Number(previewBalicekDekoraceCelkem || 0)
                      : (isPredplatneSelected
                          ? (Number(dekoraceCastka || 0) * Number(previewDekoracePocet || 0))
                          : Number(dekoraceCastka || 0))
                  )}</strong></div>
                  <div>Celkem bez DPH: <strong>{formatCena(Number(previewCelkemBezDph || 0))}</strong></div>
                  <div>DPH 21 %: <strong>{formatCena(Number(previewDph || 0))}</strong></div>
                  <div style={{ fontSize: 16 }}>
                    Orientačně celkem s DPH: <strong>{formatCena(Number(previewCelkemSDph || 0))}</strong>
                  </div>
                </div>
              ) : (
                <div style={{ color: "#475569", fontSize: 14 }}>
                  U této služby bude konečná cena stanovena individuálně po přijetí objednávky a posouzení rozsahu prací.
                </div>
              )}

              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 10,
                  background: "#fff7ed",
                  color: "#9a3412",
                  fontSize: 14,
                  lineHeight: 1.45,
                }}
              >
                Uvedená cena je pouze orientační. Skutečná cena bude potvrzena až po přijetí objednávky a po zkonzultování rozsahu prací.
              </div>
            </div>
          )}
          </div>
        </form>
      </div>
    </div>
  );
}
